import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Tooltip from "./kit-mdtw/tooltip/Tooltip";
import { moveColumn, sameColumnOrder } from "./columnOrder";

// Componente genérico de "Columnas visibles". Se comparte entre las tablas de
// Postulaciones, Formularios, Selección Masiva y Selección Profesional: recibe la
// lista de columnas configurables y el conjunto visible actual, y notifica los
// cambios respetando el orden de `columns`. Mantiene siempre al menos una
// columna visible. Con `onReorder`, además se pueden arrastrar las columnas para
// cambiar su orden (Postulaciones y Formularios); `columns` llega entonces en el
// orden del usuario.
export interface ColumnDefinition {
  key: string;
  label: string;
}

interface ColumnVisibilityMenuProps {
  columns: ColumnDefinition[];
  visibleKeys: string[];
  onChange: (keys: string[]) => void;
  isSaving?: boolean;
  isSynchronized?: boolean | null;
  /**
   * "pill" (por defecto) es el botón rectangular con texto de Postulaciones.
   * "icon" lo dibuja como botón circular con ícono (clase `button-actions`), para
   * las tablas de Selección Masiva/Profesional, donde el resto de las acciones son
   * botones circulares.
   */
  variant?: "pill" | "icon";
  /** Activa el drag and drop: recibe TODAS las claves en el nuevo orden. */
  onReorder?: (keys: string[]) => void;
  /** Orden por defecto, para ofrecer "Orden original" cuando el usuario lo cambió. */
  defaultOrder?: string[];
}

const ColumnVisibilityMenu = ({
  columns,
  visibleKeys,
  onChange,
  isSaving = false,
  isSynchronized = null,
  variant = "pill",
  onReorder,
  defaultOrder,
}: ColumnVisibilityMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draggedKey, setDraggedKey] = useState<string | null>(null);
  const [dropTargetKey, setDropTargetKey] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  const allKeys = columns.map(({ key }) => key);

  const toggleColumn = (key: string) => {
    if (visibleKeys.includes(key)) {
      if (visibleKeys.length === 1) return;
      onChange(visibleKeys.filter((current) => current !== key));
    } else {
      onChange(allKeys.filter((columnKey) => columnKey === key || visibleKeys.includes(columnKey)));
    }
  };

  const someHidden = visibleKeys.length < columns.length;
  const canReorder = Boolean(onReorder);
  const isCustomOrder = Boolean(defaultOrder) && !sameColumnOrder(allKeys, defaultOrder ?? []);

  const endDrag = () => {
    setDraggedKey(null);
    setDropTargetKey(null);
  };

  const dropOn = (targetKey: string) => {
    if (onReorder && draggedKey && draggedKey !== targetKey) {
      onReorder(moveColumn(allKeys, draggedKey, targetKey));
    }
    endDrag();
  };

  // Alternativa de teclado al arrastre: Alt + flecha arriba/abajo sobre la casilla.
  const moveByKeyboard = (key: string, offset: number) => {
    const target = allKeys[allKeys.indexOf(key) + offset];
    if (onReorder && target) onReorder(moveColumn(allKeys, key, target));
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {variant === "icon" ? (
        <Tooltip text="Columnas visibles">
          <button
            type="button"
            className="button-actions active-gris"
            onClick={() => setIsOpen((current) => !current)}
            aria-label="Columnas visibles"
            aria-haspopup="menu"
            aria-expanded={isOpen}
            style={{ position: "relative" }}
          >
            <ViewColumnIcon style={{ fontSize: 24 }} />
            {someHidden && (
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  top: "2px",
                  right: "2px",
                  width: "9px",
                  height: "9px",
                  borderRadius: "50%",
                  background: "#0065DD",
                  border: "2px solid #fff",
                }}
              />
            )}
          </button>
        </Tooltip>
      ) : (
        <motion.button
          type="button"
          onClick={() => setIsOpen((current) => !current)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "7px",
            padding: "7px 13px",
            borderRadius: "8px",
            border: isOpen ? "1.5px solid #0065DD" : "1px solid #e2e8f0",
            background: isOpen ? "#eff6ff" : "#fff",
            color: isOpen ? "#0065DD" : "#475569",
            fontFamily: "Roboto, sans-serif",
            fontSize: "13px",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          <ViewColumnIcon style={{ fontSize: 17 }} />
          Columnas
          <span style={{ fontSize: "10px", fontWeight: 700, color: "#0065DD" }}>
            {visibleKeys.length}/{columns.length}
          </span>
        </motion.button>
      )}

      {isOpen && (
        <div
          role="menu"
          aria-label="Configurar columnas visibles"
          style={{
            position: "absolute",
            top: "calc(100% + 7px)",
            left: 0,
            zIndex: 60,
            width: "245px",
            padding: "10px",
            borderRadius: "10px",
            border: "1px solid #dbe3ee",
            background: "#fff",
            boxShadow: "0 14px 34px rgba(15,23,42,0.18)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px", padding: "0 3px" }}>
            <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "11px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Columnas visibles
            </span>
            {canReorder && isCustomOrder && defaultOrder && (
              <button
                type="button"
                onClick={() => onReorder?.(defaultOrder)}
                title="Vuelve al orden de columnas por defecto"
                style={{ border: "none", background: "none", color: "#64748b", fontSize: "11px", fontWeight: 600, cursor: "pointer", marginLeft: "auto", marginRight: "6px" }}
              >
                Orden original
              </button>
            )}
            <button
              type="button"
              onClick={() => onChange(allKeys)}
              disabled={visibleKeys.length === columns.length}
              style={{ border: "none", background: "none", color: "#0065DD", fontSize: "11px", fontWeight: 600, cursor: "pointer", opacity: visibleKeys.length === columns.length ? 0.4 : 1 }}
            >
              Mostrar todas
            </button>
          </div>
          <div style={{ maxHeight: "315px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "2px" }}>
            {columns.map((column) => {
              const checked = visibleKeys.includes(column.key);
              const isLastVisible = checked && visibleKeys.length === 1;
              const isDragged = draggedKey === column.key;
              const isDropTarget = dropTargetKey === column.key && draggedKey !== column.key;
              const dropFromBelow = isDropTarget && draggedKey !== null
                && allKeys.indexOf(draggedKey) > allKeys.indexOf(column.key);
              return (
                <label
                  key={column.key}
                  title={isLastVisible ? "Debe quedar al menos una columna visible" : undefined}
                  draggable={canReorder}
                  onDragStart={canReorder ? (event) => {
                    event.dataTransfer.effectAllowed = "move";
                    event.dataTransfer.setData("text/plain", column.key);
                    setDraggedKey(column.key);
                  } : undefined}
                  onDragOver={canReorder ? (event) => {
                    if (!draggedKey) return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                    if (dropTargetKey !== column.key) setDropTargetKey(column.key);
                  } : undefined}
                  onDrop={canReorder ? (event) => {
                    event.preventDefault();
                    dropOn(column.key);
                  } : undefined}
                  onDragEnd={canReorder ? endDrag : undefined}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "9px",
                    padding: canReorder ? "7px 8px 7px 2px" : "7px 8px",
                    borderRadius: "7px",
                    background: isDropTarget ? "#eff6ff" : checked ? "#f8fbff" : "transparent",
                    color: checked ? "#1e3a5f" : "#64748b",
                    cursor: isLastVisible ? "not-allowed" : canReorder ? "grab" : "pointer",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "12.5px",
                    opacity: isDragged ? 0.45 : 1,
                    // Línea azul donde va a caer la columna.
                    boxShadow: isDropTarget ? `inset 0 ${dropFromBelow ? "2px" : "-2px"} 0 #0065DD` : "none",
                  }}
                >
                  {canReorder && (
                    <DragIndicatorIcon aria-hidden="true" style={{ fontSize: 16, color: "#94a3b8", flexShrink: 0 }} />
                  )}
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={isLastVisible}
                    onChange={() => toggleColumn(column.key)}
                    onKeyDown={canReorder ? (event) => {
                      if (!event.altKey || (event.key !== "ArrowUp" && event.key !== "ArrowDown")) return;
                      event.preventDefault();
                      moveByKeyboard(column.key, event.key === "ArrowUp" ? -1 : 1);
                    } : undefined}
                    aria-label={canReorder ? `${column.label} (Alt + flechas para moverla)` : undefined}
                    style={{ accentColor: "#0065DD", width: "15px", height: "15px" }}
                  />
                  {column.label}
                </label>
              );
            })}
          </div>
          <div style={{ marginTop: "8px", padding: "8px 5px 2px", borderTop: "1px solid #edf2f7", color: "#94a3b8", fontFamily: "Roboto, sans-serif", fontSize: "10.5px", lineHeight: 1.35 }}>
            {canReorder && <div style={{ marginBottom: "3px" }}>Arrastra las columnas para cambiar su orden.</div>}
            {isSaving
              ? "Guardando en tu cuenta…"
              : isSynchronized === false
                ? "Guardado localmente; sincronización pendiente."
                : "La selección se sincroniza con tu cuenta."}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnVisibilityMenu;
